import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { ServiceAction } from '../../actions';
import ServiceInfoView from '../../components/service/ServiceInfoView';
import ServiceComments from '../../components/service/ServiceInfoComments';
import ServiceInfoAttachments from '../../components/service/ServiceInfoAttachments';
import ServiceClaimCategory from '../../components/service/ServiceClaimCategory';
import ServiceInfoLogs from '../../components/service/ServiceInfoLogs';
import ServiceReplacement from '../../components/service/ServiceReplacement';
import SiteInfo from '../../components/site/SiteInfo';
import ProductErrorInfo from '../../components/product/ProductErrorInfo';
import ServiceProductErrorRegister from '../../components/service/ServiceProductErrorRegister';
import { AppHeader } from '../../components/common';
import { ServiceStatusIcon } from '../../components/service/ServiceCard';
import { NoRecord } from '../HomePage';
import { Translate as t } from '../../config/Translate';
import { Loading } from '../../components/common/Loading';
import { readSessionUser } from '../../lib/sessionUser';
import { getEntryPathname, isDeepLinkEntry, resolveTicketRedirect } from '../../lib/ticketRoute';

const ServiceDetail = ({ GetServiceByTicketKey, service }) => {
	const { ticketKey, view } = useParams();

	// IMP-448: 알림 메일 티켓 링크(/Service/:ticketKey)로 **주소를 직접 열고** 들어온 세션이
	// 어드민 권한이면 어드민 티켓 화면으로 승격한다(단방향). 앱 안에서 링크로 이동해 온 경우는
	// 승격하지 않는다 — 테크 포털 목록은 권한과 무관하게 /Service/ 로 링크하므로(ServiceCard.js:137)
	// 승격을 걸면 어드민이 테크 포털 티켓 화면에 머물 수 없다. 판정은 ticketRoute.js SSoT.
	// SPA <Redirect> 를 쓰지 않는 이유: app.js:133 의 isAdmin 이 마운트 시점 pathname 으로 고정되는
	// state 라 어드민 셸(메뉴/레이아웃)이 새로 로드되지 않는다(app.js:189,199 hard-reload 선례).
	// readSessionUser: AuthRoute 와 동일하게 localStorage 기준(redux mapStateToProps 미사용).
	const redirectTo = resolveTicketRedirect({
		user: readSessionUser(),
		ticketKey,
		currentPathname: typeof window !== 'undefined' ? window.location?.pathname : undefined,
		entryPathname: getEntryPathname(),
		isDeepLinkEntry: isDeepLinkEntry(),
	});

	const [tab, setTab] = useState(view || "comment");
	const [maintab, setMainTab] = useState("info");
	const [isLoading, setIsLoading] = useState(true);
	const [editable, setEditable] = useState(false);

	useEffect(() => {
		// 조용히 이동 — 사용자 입력을 제거·차단하는 가드가 아니라 동선 이동이므로 warn 불필요.
		// replace: assign 은 히스토리를 쌓아 뒤로가기 시 /Service/:ticketKey 로 돌아왔다가 즉시
		// 재승격되어 이전 화면으로 영영 못 돌아간다(R1 M-2).
		if (redirectTo) {
			window.location.replace(redirectTo);
		}
	}, [redirectTo]);

	useEffect(() => {
		if (service && service.ticketKey === ticketKey) {
			setIsLoading(false);
		}
	}, [service?.ticketKey, ticketKey])

	useEffect(() => {
		// IMP-448: 리다이렉트 예정이면 헛도는 fetch 생략(중복 fetch/깜빡임 방지).
		if (redirectTo) return undefined;
		// BUG-156: 데이터 존재가 아닌 fetch 완료 기준으로 로딩 해제(not-found 시 무한/지연 스피너 방지)
		let isMounted = true;
		setIsLoading(true);
		// thunk 는 내부 try/catch 로 에러를 이미 처리(AlertAction)하므로 여기 catch 는
		// unhandled rejection 재전파만 차단한다(§FE 방어 8-2).
		Promise.resolve(GetServiceByTicketKey(ticketKey)).catch(() => {}).finally(() => {
			if (isMounted) setIsLoading(false);
		});
		return () => { isMounted = false; };
	}, [ticketKey, redirectTo])

	// F-021: 라우트 게이트 (AdminServiceInfo.js:43·:117 선례) — 라우트 param 의 ticketKey 와
	// redux service 가 일치하고 _id 가 있을 때만 service 데이터 의존 섹션을 렌더한다.
	// 액션의 CLEAER 가 경합 가드로 skip 돼 다른 티켓(A)이 redux 에 남아 있어도
	// URL=B 화면에 A 가 그려지는 stale 렌더/wrong-record write 를 여기서 차단한다.
	// fail-closed: 라우트 param(/Service/:ticketKey?)이 optional 이라 둘 다 undefined 면
	// 느슨한 비교(!=)로는 게이트가 열린다 — ticketKey 부재 자체를 nodata 로 처리.
	const nodata = !ticketKey || String(ticketKey) !== String(service?.ticketKey ?? '');
	const hasService = !nodata && !!service?._id;

	const getLengthStr = (objArr) => {
		if (objArr) {
			return objArr.length > 0 ? "(" + objArr.length + ")" : "";
		}
		return "";
	}

	const mainTabList = [
		{ code: "info", title: t('common.label.overview'), icon: "info" },
		{ code: "status", title: t('common.label.detail'), icon: "visibility" },
		{ code: "error", title: t('common.label.error'), icon: "error_outline" },
		{ code: "site", title: t('site.title'), icon: "location_city" }
	]

	const tabList = [
		// F-021: nav 자체가 hasService 게이트 안이지만, 라벨 집계도 이중 방어로 hasService 조건 유지
		{ code: "comment", title: t('common.label.comments') + getLengthStr(hasService ? service.comments : null) },
		{ code: "attachments", title: t('common.label.attachments') + getLengthStr(hasService ? service.attachments : null) },
		{ code: "logs", title: t('common.label.logs') + getLengthStr(hasService ? service.histories : null) }
	]

	if (isLoading) {
		return (
			<div id="appCapsule">
				<div className="section mt-2 text-center">
					<Loading />
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="appHeader no-border">
				{/* F-021: 뒤로가기 등 골격은 유지하되 service 파생 표기는 hasService 일 때만 (stale 헤더 차단) */}
				<AppHeader
					leadIcon={hasService ? ServiceStatusIcon : null}
					item={hasService ? service : null}
					title={hasService ? service.summary : t('service.label.serviceInformation')}
					subtitle={hasService ? (service.sn + ((service.errorCode != undefined) ? " / " + service.errorCode : "")) : ""} />
				<div className="right">
				</div>
			</div>
			<div id="appCapsule">
				{/* F-021: nodata 시 탭 nav 도 숨겨 AdminServiceInfo nodata 분기(헤더+NoRecord 만)와 UI 일치 */}
				{hasService &&
					<div className="section mb-2">
						<ul className="nav nav-tabs style2 mt-1 mb-1">
							{mainTabList.map((x,idx) =>
								<li key={`srv_${idx}`} className="nav-item">
									<button key={x.code}
										className={maintab == x.code ? "nav-link active" : "nav-link"} onClick={() => { setMainTab(x.code); }}>
										{x.title}
									</button>
								</li>
							)}
						</ul>
					</div>
				}
				{/* F-021: service 데이터 의존 섹션은 hasService 게이트 안에서만 렌더 */}
				{(hasService && maintab == "info") && <ServiceInfoView service={service} editable={editable} onEdit={() => setEditable(true)} onCancel={() => setEditable(false)} callBackUpdate={() => setEditable(false)} />}

				{(hasService && maintab == "status") &&
					<>
						<div className="section mt-2 mb-2">
							<div className="card">
								<ServiceClaimCategory editable={editable} key={service} />
								<ServiceReplacement service={service} editable={editable} />
							</div>
						</div>
					</>
				}
				{(hasService && maintab == "error") && <>
					{service.productErrorCodeId && <ProductErrorInfo productErrorCodeId={service.productErrorCodeId} sn={service.sn} />}
					{!service.productErrorCodeId && <ServiceProductErrorRegister serviceId={service._id} sn={service.sn} />}
				</>
				}
				{(hasService && maintab == "site" && service.siteId) && <SiteInfo siteId={service.siteId} showTitle={true} />}
				{(hasService && maintab == "site" && !service.siteId) && <NoRecord
					title="Not Installed"
					contents={"This Product( " + service.sn + " ) is not installed"} />}
				{/* F-021: 불일치/부재 시 AdminServiceInfo nodata 관례(NoRecord) — 기존 키만 사용 */}
				{!hasService && <div className="section mt-2 mb-2"><NoRecord /></div>}
				{hasService &&
					<div className="section mt-2 mb-2">
						<div className="card">
							<ul className="nav nav-tabs lined">
								{tabList.map((x, idx) =>
									<li key={idx} className="nav-item">
										<a href
											key={x.code}
											className={tab == x.code ? "nav-link active" : "nav-link"}
											onClick={() => { setTab(x.code); }} >{x.title}</a>
									</li>
								)}
							</ul>
							{tab == "comment" && <ServiceComments service={service} />}
							{tab == "attachments" && <ServiceInfoAttachments service={service} />}
							{tab == "logs" && <ServiceInfoLogs service={service} />}
						</div>
					</div>
				}
			</div>
		</>
	)
}

const mapState = (state) => {
	const service = state.ServiceReducer.service;
	return { service };
};

const mapDispatch = dispatch => ({
	GetServiceByTicketKey: (ticketKey) => dispatch(ServiceAction.GetServiceByTicketKey(ticketKey)),
})

export default connect(mapState, mapDispatch)(ServiceDetail);